import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import { useDispatch, useSelector } from 'react-redux';
import { ALL_TIME_ZONES } from './Constants';
import {
  getTenantDetails,
  ingestCommunity,
  ingestCommunityGet,
  onboardCommunities as onboardCommunitiesAction,
  createCommunityConfigsSelfServe,
  loadSourcesForVoiceAISelfServe
} from '../../../Redux/Actions/SelfServe';
import {
  addCommunityName,
  addCompanyCode,
  addKnockRelatedDetails,
  addPropertyCode,
  addRentcafeApiToken,
  addSelectedApartment,
  addTimeZone,
  listingCommunityNotification,
  openCommunity,
  removeSelectedApartment
} from '../../../Redux/Reducer/SelfServe';
import Grid from '../../../Component/Common/CommunityGrid';
import CommunityInformation from './communityInformation';
import ReadinessCheckDialog from './ReadinessCheckDialog';
import { GlobalContext } from '../../../utils/ContextProviders/GlobalContextProvider';

interface Community {
  _id: string;
  name: string;
  addressId?: { fullAdd?: string };
  communityPhone?: string;
  createdAt?: string | number;
  timeZone?: string;
  tenantId?: string | number;
  meta?: {
    knockCommunity?: { communityId?: string; propertyId?: string };
    knockKelseyUserName?: string;
    knockHouseAgentUserName?: string;
    PropertyCode?: string;
  };
  integrations?: boolean;
  isLive?: boolean;
  leadIngestion?: boolean;
  isCLOProperty?: boolean;
}

interface OnboardNotification {
  upload: boolean;
  success: boolean;
  msg: string;
}

interface CommunitiesProps {
  tenantId: string;
  crmSettings?: any;
}

const Communities: React.FC<CommunitiesProps> = ({ tenantId, crmSettings }) => {
  const adminData = useSelector((state: any) => state.Admin);
  const selfServe = useSelector((state: any) => state.SelfServe);
  const integrationSettings = selfServe?.userData?.data?.integrationSettings;
  const pmsValue: string | undefined =
    integrationSettings && integrationSettings?.pms;
  const pmcId: string | undefined =
    integrationSettings && integrationSettings?.pmcId;
  const [communityId, setCommunityId] = useState('');
  const [onboardNotification, setOnboardNotification] =
    useState<OnboardNotification>({
      upload: false,
      success: false,
      msg: ''
    });
  const [onboardedCommunities, setOnboardedCommunities] = useState<string[]>(
    []
  );
  const [readinessDialogOpen, setReadinessDialogOpen] = useState(false);
  const { apartmentData, selectedApartment } = selfServe;
  const [info, infoPopUPHandler] = useState(false);
  const [searchVal, setSearchVal] = useState('');
  const [showFilteredApartment, setShowFilteredApartment] =
    useState<Community[]>(apartmentData);
  const [ingestSnackbar, setIngestSnackbar] = useState(false);
  const [forceIngestDialogOpen, setForceIngestDialogOpen] = useState(false);
  const { userData } = useContext(GlobalContext);
  const dispatch = useDispatch<any>();
  const handleChangeSearch = (value: string) => setSearchVal(value);
  const gridClickHandler = (
    id: string,
    communityName: string,
    timeZone: { label: string; value: string } | false,
    cmnId: string | false,
    kelseyId: string | false,
    houseId: string | false,
    CompanyCode: string | false,
    PropertyCode: string | false,
    rentcafeApiToken: string | false
  ) => {
    dispatch(openCommunity());

    if (communityName) {
      dispatch(addCommunityName(communityName));
    }
    if (timeZone) {
      dispatch(addTimeZone({ value: timeZone, disable: true }));
    }
    if (PropertyCode) {
      dispatch(addPropertyCode({ value: PropertyCode, disable: true }));
    }
    if (CompanyCode) {
      dispatch(addCompanyCode({ value: CompanyCode, disable: true }));
    }
    if (rentcafeApiToken) {
      dispatch(addRentcafeApiToken({ value: rentcafeApiToken, disable: true }));
    }
    if (cmnId && kelseyId && houseId) {
      dispatch(
        addKnockRelatedDetails({
          knockId: cmnId,
          kelsey: kelseyId,
          house: houseId
        })
      );
    }
    setCommunityId(id);
    infoPopUPHandler(true);
  };

  const ingestCommunitiesOnLoad = async (
    notification?: OnboardNotification,
    force = false
  ) => {
    if (tenantId) {
      let payload: any;
      if (pmsValue === 'yardi') {
        const {
          yardiUrl,
          yardiId,
          userName,
          password,
          database,
          platform,
          pms
        } = integrationSettings;
        payload = {
          pms: 'yardi',
          payload: {
            _id: tenantId,
            name: selfServe?.userData?.data?.name,
            integrationSettings: {
              yardiUrl,
              yardiId,
              userName,
              password,
              database,
              platform,
              pms
            }
          }
        };
      }
      if (pmsValue === 'entrata') {
        payload = {
          pms: 'entrata',
          payload: {
            _id: tenantId,
            pmcid: pmcId
          }
        };
      }
      if (pmsValue === 'realpage') {
        payload = {
          pms: 'realpage',
          payload: {
            _id: tenantId,
            pmcid: pmcId
          },
          // Force-ingest creates communities even when RealPage has no published
          // unit catalog yet (0-unit shells). Only RealPage honors this. ZUM-2873.
          force
        };
      }
      if (payload) {
        try {
          await dispatch(ingestCommunity(payload)).unwrap();
          // Post-onboard calls pass a notification; show only the
          // "Onboarded successfully" snackbar then, not the ingest one.
          if (!notification) {
            setIngestSnackbar(true);
          }
        } catch {
          // error handled by global error handler
        }
      }
    }
    if (notification?.success) {
      setOnboardNotification(notification);
    }
    dispatch(ingestCommunityGet(tenantId));
  };
  useEffect(() => {
    // An empty array is a valid result (e.g. every community was just
    // onboarded) and must clear the grid.
    if (apartmentData) {
      setShowFilteredApartment([...apartmentData]);
    }
  }, [apartmentData]);
  useEffect(() => {
    if (searchVal) {
      const updatedData = apartmentData.filter(
        ({ name }: Community) =>
          name?.includes(searchVal) || name?.toLowerCase().includes(searchVal)
      );
      setShowFilteredApartment([...updatedData]);
    } else {
      setShowFilteredApartment([...apartmentData]);
    }
  }, [searchVal]);
  useEffect(() => {
    if (adminData?.users?.data) {
      dispatch(getTenantDetails(tenantId));
    }
  }, [adminData?.users]);

  useEffect(() => {
    if (tenantId) {
      dispatch(ingestCommunityGet(tenantId));
    }
  }, [userData]);

  const closeNotification = () => {
    if (onboardNotification?.upload) {
      setOnboardNotification(prev => ({
        ...prev,
        success: false,
        upload: false
      }));
    } else {
      dispatch(listingCommunityNotification());
    }
  };

  const onboardCommunities = (data: string) => {
    setOnboardedCommunities(prev => {
      const filter = apartmentData.findIndex(
        (item: Community) =>
          item?._id === data && item?.tenantId?.toString() === tenantId
      );
      dispatch(addSelectedApartment(apartmentData[filter]));
      return [...prev, data];
    });
  };
  const removeOnboard = (data: string) => {
    setOnboardedCommunities(prev => {
      const updatedData = prev.filter(item => item !== data);
      const filter = selectedApartment?.filter(
        (item: Community) =>
          item?._id !== data && item?.tenantId?.toString() === tenantId
      );
      dispatch(removeSelectedApartment(filter));
      return updatedData;
    });
  };

  const handleOnboard = () => {
    setReadinessDialogOpen(true);
  };

  const handleOnboardFromDialog = async (idsToOnboard: string[]) => {
    setReadinessDialogOpen(false);
    try {
      await dispatch(
        onboardCommunitiesAction({
          tenantId,
          communityIds: idsToOnboard,
          ingestCommunitiesOnLoad: ingestCommunitiesOnLoad
        })
      ).unwrap();
    } catch {
      // Onboard failed — keep the selection so the user can retry.
      return;
    }
    setOnboardedCommunities(prev =>
      prev.filter(id => !idsToOnboard.includes(id))
    );
    idsToOnboard.forEach(id => {
      dispatch(createCommunityConfigsSelfServe({ payload: id }));
      dispatch(loadSourcesForVoiceAISelfServe({ payload: id }));
    });
  };

  const sortedCommunities = showFilteredApartment
    ?.slice()
    .sort((a: Community, b: Community) =>
      (a?.name || '').localeCompare(b?.name || '')
    );

  return (
    <Paper sx={{ bgcolor: 'background.paper', pt: '20px' }} elevation={0}>
      {/* Top Bar: Search + Actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: '20px',
          mb: '16px'
        }}
      >
        <TextField
          onChange={e => handleChangeSearch(e.target.value)}
          placeholder="Search communities"
          variant="outlined"
          size="small"
          value={searchVal}
          sx={{ width: '300px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            )
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={() => ingestCommunitiesOnLoad()}
            sx={{ textTransform: 'none' }}
          >
            Ingest Communities
          </Button>

          {pmsValue === 'realpage' && (
            <Button
              variant="text"
              onClick={() => setForceIngestDialogOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Force ingest
            </Button>
          )}

          {showFilteredApartment?.length > 0 && (
            <Button
              variant="contained"
              onClick={handleOnboard}
              disabled={onboardedCommunities.length === 0}
            >
              Begin Onboarding Checks
            </Button>
          )}
        </Box>
      </Box>

      {/* Community Grid */}
      <Box
        sx={{
          px: '20px',
          pb: '20px',
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          maxHeight: '60vh',
          overflowY: 'auto'
        }}
      >
        {selfServe?.apiLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : sortedCommunities && sortedCommunities.length ? (
          sortedCommunities.map((item: Community) => (
            <Grid
              key={item?._id}
              apptId={item?._id}
              header={item.name}
              address={item.addressId}
              communityPhone={item?.communityPhone}
              timeLabel={item.createdAt}
              error={false}
              clickHandler={() =>
                gridClickHandler(
                  item?._id,
                  item?.name,
                  item?.timeZone
                    ? {
                        label: ALL_TIME_ZONES[item.timeZone],
                        value: item.timeZone
                      }
                    : false,
                  item?.meta?.knockCommunity?.communityId ||
                    item?.meta?.knockCommunity?.propertyId ||
                    false,
                  item?.meta?.knockKelseyUserName || false,
                  item?.meta?.knockHouseAgentUserName || false,
                  integrationSettings?.CompanyCode || false,
                  item?.meta?.PropertyCode || false,
                  integrationSettings?.rentcafeApiV2Token || false
                )
              }
              onboardCommunities={onboardCommunities}
              removeOnboard={removeOnboard}
              timeZone={item?.timeZone}
              isCLOProperty={item?.isCLOProperty}
            />
          ))
        ) : (
          <Typography sx={{ color: 'text.secondary', py: '40px' }}>
            No ingested communities found.
          </Typography>
        )}
      </Box>

      <CommunityInformation
        crmSettings={crmSettings}
        apartmentId={communityId}
        apartmentData={apartmentData}
        info={info}
        infoPopUPHandler={infoPopUPHandler}
      />

      <ReadinessCheckDialog
        open={readinessDialogOpen}
        onClose={() => setReadinessDialogOpen(false)}
        onOnboard={handleOnboardFromDialog}
        communityIds={onboardedCommunities}
        communities={selectedApartment || []}
        tenantId={tenantId}
      />

      {/* Notification Snackbar */}
      {(!selfServe.listingCommunity || onboardNotification?.upload) && (
        <Snackbar
          autoHideDuration={4000}
          open={!selfServe.listingCommunity || onboardNotification?.upload}
          onClose={closeNotification}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            severity={
              onboardNotification?.upload && onboardNotification?.success
                ? 'success'
                : 'error'
            }
            onClose={closeNotification}
          >
            {onboardNotification?.upload && onboardNotification?.success
              ? 'Onboarded successfully'
              : onboardNotification?.upload
                ? 'Upload failed'
                : 'Something went wrong'}
          </Alert>
        </Snackbar>
      )}

      <Snackbar
        open={ingestSnackbar}
        autoHideDuration={5000}
        onClose={() => setIngestSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setIngestSnackbar(false)}
        >
          Communities ingestion triggered successfully and will be ingested in
          5-10 minutes.
        </Alert>
      </Snackbar>

      <Dialog
        open={forceIngestDialogOpen}
        onClose={() => setForceIngestDialogOpen(false)}
      >
        <DialogTitle>Force ingest communities?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Force ingest creates communities even when RealPage has not yet
            published their unit catalog. Such communities are created as 0-unit
            shells (no availability or pricing) until units are published, at
            which point a normal ingest backfills them. Only use this to unblock
            a launch when a property is approved in RealPage but its units
            aren&apos;t live yet.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceIngestDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setForceIngestDialogOpen(false);
              ingestCommunitiesOnLoad(undefined, true);
            }}
          >
            Force ingest
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Communities;
